import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  Optional,
  Self,
  ViewChild,
  inject,
} from '@angular/core';
import { ControlContainer, FormGroupDirective, NgControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Select } from 'primeng/select';
import { PropertyMortgageSelectItem } from '../../../models/property-mortgage-select-item.model';
import { AbstractControlToFormControlPipe } from '../../../pipes/abstract-control-to-form-control.pipe';
import { JoinPipe } from '../../../pipes/join.pipe';
import { IconComponent } from '../../icon/icon.component';
import { LabeledValueComponent } from '../../labeled-value/labeled-value.component';

@Component({
  selector: 'shared-property-mortgage-select',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AbstractControlToFormControlPipe,
    LabeledValueComponent,
    JoinPipe,
    IconComponent,
    ButtonModule,
    Select,
  ],
  templateUrl: './property-mortgage-select.component.html',
  styleUrls: ['./property-mortgage-select.component.scss'],
  providers: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class PropertyMortgageSelectComponent implements AfterViewInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @Input() inline = false;

  @Input() set propertyMortgageSelectItems(propertyMortgageSelectItems: PropertyMortgageSelectItem[]) {
    this._propertyMortgageSelectItems = propertyMortgageSelectItems;

    if (this.ngControl.value) {
      this.propertyIndex = propertyMortgageSelectItems.findIndex((property) => property.propertyId === this.ngControl.value?.propertyId);
      this.ngControl.control?.setValue(propertyMortgageSelectItems[this.propertyIndex]);
    } else {
      if (this.ngControl.control == null) return;
      this.ngControl.control.setValue(propertyMortgageSelectItems[0]);
      this.changeDetectorRef.detectChanges();
    }
  }

  get propertyMortgageSelectItems(): PropertyMortgageSelectItem[] {
    return this._propertyMortgageSelectItems;
  }

  @ViewChild('inlinePropertyList') inlinePropertyListElementRef: ElementRef | undefined;

  @HostListener('swipeleft') swipeLeft(): void {
    this.nextProperty();
  }

  @HostListener('swiperight') swipeRight(): void {
    this.previousProperty();
  }

  propertyIndex = 0;
  private _propertyMortgageSelectItems: PropertyMortgageSelectItem[] = [];

  constructor(@Self() @Optional() public ngControl: NgControl) {
    if (this.ngControl) {
      // trick it into thinking control has a valueAccessor, we don't need it because child has
      this.ngControl.valueAccessor = {
        writeValue(): void {
          // do nothing
        },
        registerOnChange(): void {
          // do nothing
        },
        registerOnTouched(): void {
          // do nothing
        },
      };
    }
  }

  ngAfterViewInit(): void {
    if (this.ngControl.value) {
      setTimeout(() => this.scrollToSelectedElement(), 300);
    }
  }

  selectItemByIndex(itemIndex: number): void {
    this.ngControl.control?.setValue(this.propertyMortgageSelectItems[itemIndex]);
    setTimeout(() => this.scrollToSelectedElement(), 10);
  }

  previousProperty(): void {
    if (this.propertyIndex === 0) {
      return;
    }
    this.propertyIndex -= 1;
    this.selectItemByIndex(this.propertyIndex);
  }

  nextProperty(): void {
    if (this.propertyIndex === this.propertyMortgageSelectItems.length - 1) {
      return;
    }
    this.propertyIndex += 1;
    this.selectItemByIndex(this.propertyIndex);
  }

  private scrollToSelectedElement(): void {
    if (this.inlinePropertyListElementRef == null) return;

    const selectedItem = this.inlinePropertyListElementRef.nativeElement.getElementsByClassName('selected')[0];
    const margin = this.inlinePropertyListElementRef.nativeElement.children[0].offsetLeft;
    this.inlinePropertyListElementRef.nativeElement.scrollTo(selectedItem.offsetLeft - margin, 0);
  }
}
